import {
  AlertCircle,
  Book,
  CreditCard,
  DollarSign,
  FileText,
  Home,
  Landmark,
  type LucideIcon,
  Settings,
  Smartphone,
  TrendingUp,
  UserCog,
  Users,
  Utensils,
  Wallet,
} from "lucide-react";
import type { z } from "zod";

import type { permissionsSchema } from "../validators/merchant-permissions";

type PermissionSet = z.infer<typeof permissionsSchema>;

export interface NavItem {
  title: string;
  url: string;
  icon?: LucideIcon;
  isActive: boolean;
  items?: {
    title: string;
    url: string;
    isActive: boolean;
  }[];
}

export interface NavSection {
  label?: string;
  items: NavItem[];
  /** If true, the section renders as a collapsible dropdown with sub-items */
  collapsible?: boolean;
  /** Icon for collapsible sections */
  icon?: LucideIcon;
  /** Whether any item in this section is active (for collapsible highlight) */
  isActive?: boolean;
}

/**
 * Check if a navigation item should be active based on current path
 * @param currentPath - Current route pathname
 * @param itemPath - Navigation item path
 * @returns Whether the item is active
 */
export function isPathActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === "/") {
    return currentPath === "/";
  }
  return currentPath.startsWith(itemPath);
}

const canView = (flag?: boolean) => flag !== false;

/**
 * Build navigation sections based on permissions
 * @param currentPath - Current route pathname
 * @param permissions - User permission set
 * @returns Array of navigation sections with labeled groups
 */
export function buildNavSections({
  currentPath,
  permissions,
}: {
  currentPath: string;
  permissions?: PermissionSet;
}): NavSection[] {
  // Build location items conditionally
  const locationItems: NavItem[] = [
    {
      title: "Overview",
      url: "/",
      icon: Home,
      isActive: currentPath === "/",
    },
    {
      title: "Locations",
      url: "/locations",
      icon: Utensils,
      isActive: currentPath.startsWith("/locations"),
    },
  ];

  if (canView(permissions?.editMenus)) {
    locationItems.push({
      title: "Menus",
      url: "/menus",
      icon: Book,
      isActive: currentPath.startsWith("/menus"),
    });
  }

  if (canView(permissions?.viewEmployees)) {
    locationItems.push({
      title: "Employees",
      url: "/employees",
      icon: Users,
      isActive: currentPath.startsWith("/employees"),
    });
  }

  if (canView(permissions?.viewAllOrders)) {
    locationItems.push({
      title: "Reports",
      url: "/reports",
      icon: TrendingUp,
      isActive: currentPath.startsWith("/reports"),
    });
  }

  const sections: NavSection[] = [
    // LOCATION-LEVEL: Operational stuff tied to physical locations
    {
      label: "Locations",
      items: locationItems,
    },

    // ACCOUNT-LEVEL: Stripe Connect (processing, money flow, compliance)
    {
      label: "Finances",
      collapsible: true,
      icon: Landmark,
      isActive:
        currentPath.startsWith("/payments") ||
        currentPath === "/payouts" ||
        currentPath === "/balances" ||
        currentPath === "/disputes" ||
        currentPath === "/documents" ||
        currentPath === "/settings/account-management",
      items: [
        {
          title: "Payments",
          url: "/payments",
          icon: CreditCard,
          isActive: currentPath.startsWith("/payments"),
        },
        {
          title: "Payouts",
          url: "/payouts",
          icon: Wallet,
          isActive: currentPath === "/payouts",
        },
        {
          title: "Balances",
          url: "/balances",
          icon: DollarSign,
          isActive: currentPath === "/balances",
        },
        {
          title: "Disputes",
          url: "/disputes",
          icon: AlertCircle,
          isActive: currentPath === "/disputes",
        },
        {
          title: "Tax Documents",
          url: "/documents",
          icon: FileText,
          isActive: currentPath === "/documents",
        },
        {
          title: "Account",
          url: "/settings/account-management",
          icon: UserCog,
          isActive: currentPath === "/settings/account-management",
        },
      ],
    },

    // SETTINGS: Platform config + Stripe Billing (subscription/licensing)
    {
      label: "Settings",
      collapsible: true,
      icon: Settings,
      isActive:
        currentPath.startsWith("/settings") &&
        currentPath !== "/settings/account-management",
      items: [
        {
          title: "Account",
          url: "/settings/account",
          isActive: currentPath === "/settings/account",
        },
        {
          title: "Organization",
          url: "/settings/organization",
          isActive: currentPath.startsWith("/settings/organization"),
        },
        {
          title: "Business Information",
          url: "/settings/business-information",
          isActive: currentPath === "/settings/business-information",
        },
        {
          title: "Billing & Usage",
          url: "/settings/billing",
          isActive:
            currentPath === "/settings/billing" ||
            currentPath === "/settings/usage",
        },
        {
          title: "Tax Categories",
          url: "/settings/tax-categories",
          isActive: currentPath === "/settings/tax-categories",
        },
        {
          title: "Sales Categories",
          url: "/settings/sales-categories",
          isActive: currentPath === "/settings/sales-categories",
        },
        {
          title: "Card Readers",
          url: "/settings/terminals",
          icon: Smartphone,
          isActive: currentPath === "/settings/terminals",
        },
      ],
    },
  ];

  return sections;
}

/**
 * Legacy function for backwards compatibility - flattens sections into items
 * @deprecated Use buildNavSections directly with section-aware components
 */
export function buildNavItems({
  currentPath,
  permissions,
}: {
  currentPath: string;
  permissions?: PermissionSet;
}): NavItem[] {
  const sections = buildNavSections({ currentPath, permissions });
  return sections.flatMap((section) => section.items);
}
